'use client';

import { isBefore } from 'date-fns';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useTradeContext } from '../../../components/ui/TradePageClient/TradePageClient';
import type { PostOptionFormParams } from '../../../features/lottery/api/v2/postOptionForm';
import type { LotteryOptionGoodsForm } from '../../../features/lottery/types';
import { newDate } from '../../../utils/date';
import styles from './OptionBar.module.scss';

export type OptionBarProps = {
  optionFormData?: LotteryOptionGoodsForm;
  goodsId: string;
  cutoffId: string;
  isEditMode?: boolean;
  lotteryHistoryId?: number;
  orderId?: string;
  postOptionForm: (params: PostOptionFormParams) => Promise<void>;
  step?: 'quantity' | 'name';
  onStepChange?: (step: 'quantity' | 'name') => void;
};

function OptionBar({
  optionFormData,
  cutoffId,
  isEditMode = false,
  lotteryHistoryId,
  orderId,
  postOptionForm,
  step = 'quantity',
  onStepChange,
}: OptionBarProps) {
  const { selectedItems } = useTradeContext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [filledInputCount, setFilledInputCount] = useState(0);
  const prevStepRef = useRef<'quantity' | 'name'>(step);

  const totalCount = Array.from(selectedItems.values()).reduce((sum, current) => sum + current, 0);

  useEffect(() => {
    if (!optionFormData) return;

    // named_selectableでstepが'quantity'の時は入力欄を監視しない
    if (optionFormData.optionInfo.type === 'named_selectable' && step === 'quantity') {
      setFilledInputCount(0);
      return;
    }

    const inputs = document.querySelectorAll(
      `input[name^="optionInput-${optionFormData.id}"], textarea[name^="optionInput-${optionFormData.id}"]`,
    );

    let count = 0;
    inputs.forEach((input) => {
      if ((input as HTMLInputElement | HTMLTextAreaElement).value.trim() !== '') {
        count++;
      }
    });
    setFilledInputCount(count);

    const handleInputChange = () => {
      let newCount = 0;
      inputs.forEach((input) => {
        if ((input as HTMLInputElement | HTMLTextAreaElement).value.trim() !== '') {
          newCount++;
        }
      });
      setFilledInputCount(newCount);
    };

    inputs.forEach((input) => {
      input.addEventListener('input', handleInputChange);
    });

    return () => {
      inputs.forEach((input) => {
        input.removeEventListener('input', handleInputChange);
      });
    };
  }, [optionFormData, totalCount, step]);

  // 選べる＆宛名オプションに入力の場合、stepが'quantity'から'name'に変わるタイミングでスクロール位置を一番上に戻す。
  // スクロール位置の制御は、step変更に伴う再レンダリングでDOM構造が切り替わった「後」に実行する必要がある。
  // イベントハンドラ内で即時にscrollToすると、まだ旧DOMのまま/レイアウト未確定のタイミングに当たり、
  // 期待どおりに先頭へ戻らないことがあるため、依存値(optionFormData, step)の更新後に走るuseEffectで扱う。
  // さらにrequestAnimationFrameでブラウザ描画直前まで待つことで、実DOM反映後の安定したタイミングで確実にスクロールする。
  useEffect(() => {
    if (!optionFormData) {
      prevStepRef.current = step;
      return;
    }

    const isStepChangedToName = prevStepRef.current === 'quantity' && step === 'name';
    prevStepRef.current = step;
    if (!isStepChangedToName) return;
    if (optionFormData.optionInfo.type !== 'named_selectable') return;

    // step変更後に実DOMへ反映されてからスクロール位置を一番上に戻す
    const rafId = window.requestAnimationFrame(() => {
      const scrollTarget = document.scrollingElement;
      if (scrollTarget) {
        scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [optionFormData, step]);

  const handleSubmit = async () => {
    if (!optionFormData) return;

    if (optionFormData?.optionInfo.type === 'named_selectable' && step === 'quantity') {
      // 数量入力完了 → 次のステップへ
      onStepChange?.('name');
      return;
    }

    try {
      // 入力データを収集
      const optionData = collectOptionData();

      // API呼び出し
      const _ = await postOptionForm({
        lotteryPrizeGoodsId: optionFormData.id,
        lotteryCutoffId: Number(cutoffId),
        optionType: optionFormData.optionInfo.type as 'named' | 'selectable' | 'named_selectable',
        optionData: optionData as never, // 型安全性のため一時的にnever使用
        orderId: orderId,
      });

      // 成功時の処理（オプション一覧ページに戻る等）
      const queryParams = new URLSearchParams();

      // lotteryHistoryIdがある場合はそれを優先的にパラメータに含める
      if (lotteryHistoryId) {
        queryParams.set('lotteryHistoryId', lotteryHistoryId.toString());
      } else {
        // lotteryHistoryIdがない場合は従来通りのパラメータを含める
        // goodsId (lotteryPrizeGoodsId) を追加
        if (optionFormData.id) {
          queryParams.set('goodsId', optionFormData.id.toString());
        }

        if (cutoffId) {
          queryParams.set('cutoffId', cutoffId); // lottery_cutoff_id
        }
      }

      // orderIdは常に渡す
      if (orderId) {
        queryParams.set('orderId', orderId); // order_id
      }

      // 遷移元コンテキスト（from）を持ち回す
      const from = searchParams.get('from');
      if (from) {
        queryParams.set('from', from);
      }

      const url = `/account/collection/option${queryParams.toString() ? `?${queryParams.toString()}` : ''}` as const;
      router.push(url);
      router.refresh();
    } catch (error) {
      console.error('オプション入力送信エラー:', error);
    }
  };

  // 入力データを収集する関数
  const collectOptionData = () => {
    if (!optionFormData) return [];

    const optionType = optionFormData.optionInfo.type;

    if (optionType === 'named') {
      // named: テキスト入力から宛名を収集
      const inputs = document.querySelectorAll(
        `input[name^="optionInput-${optionFormData.id}-"], textarea[name^="optionInput-${optionFormData.id}-"]`,
      );
      const data: Array<{ hash: string | null; value: string }> = [];

      // 全てのinput/textareaから値を収集（空文字も含める）
      inputs.forEach((input) => {
        const value = (input as HTMLInputElement | HTMLTextAreaElement).value.trim();
        data.push({
          hash: null, // 新規入力
          value: value || '', // 空文字の場合も含める
        });
      });

      return data;
    }

    if (optionType === 'selectable') {
      // selectable: 選択された数量を収集
      const data: Array<{ hash: string; value: string }> = [];

      if (optionFormData.optionInfo.selectable) {
        optionFormData.optionInfo.selectable.forEach((selectableItem, index) => {
          const goodId = `${optionFormData.id}-${index}`;
          const count = selectedItems.get(goodId) || 0;
          if (count > 0) {
            data.push({
              hash: selectableItem.hash, // 正しい選択肢hash
              value: count.toString(),
            });
          }
        });
      }

      return data;
    }

    if (optionType === 'named_selectable') {
      // named_selectable: 選択肢と宛名を収集
      const data: Array<{ hash: string; notes: Array<{ value: string }> }> = [];

      if (optionFormData.optionInfo.namedSelectable) {
        optionFormData.optionInfo.namedSelectable.forEach((namedSelectableItem, index) => {
          const goodId = `${optionFormData.id}-${index}`;
          const count = selectedItems.get(goodId) || 0;

          if (count > 0) {
            // この項目の入力フィールドを取得
            const itemInputs = document.querySelectorAll(
              `input[name^="optionInput-${optionFormData.id}-${index}-"], textarea[name^="optionInput-${optionFormData.id}-${index}-"]`,
            );
            const notes: Array<{ value: string }> = [];

            itemInputs.forEach((input) => {
              const value = (input as HTMLInputElement | HTMLTextAreaElement).value.trim();
              if (value) {
                notes.push({ value });
              }
            });

            data.push({
              hash: namedSelectableItem.hash,
              notes: notes,
            });
          }
        });
      }

      return data;
    }

    return [];
  };

  const handleGoBack = () => {
    router.back();
  };

  // 期日チェック
  const isPastDeadline = () => {
    if (!optionFormData) return false;
    return isBefore(newDate(optionFormData.optionCutoffAt), newDate());
  };

  return (
    <>
      <div className={styles.componentStyle}>
        <div className={styles.container}>
          <div className={styles.buttonWrapper}>
            <button type='button' className={styles.backButton} onClick={handleGoBack}>
              戻る
            </button>
            {!(isEditMode && isPastDeadline()) && (
              <div className={styles.confirmButtonWrapper}>
                <button
                  type='button'
                  className={styles.confirmButton}
                  onClick={handleSubmit}
                  color={(() => {
                    if (!optionFormData) return 'gray';

                    if (optionFormData.optionInfo.type === 'selectable') {
                      return totalCount === optionFormData.totalAcquiredCount ? 'primary' : 'gray';
                    }

                    if (optionFormData.optionInfo.type === 'named_selectable') {
                      // stepが'quantity'の場合は数量入力段階なのでtotalCountをチェック
                      // stepが'name'の場合は宛名入力段階なのでfilledInputCountをチェック
                      const currentCount = step === 'quantity' ? totalCount : filledInputCount;
                      const remainingCount = Math.max(0, optionFormData.totalAcquiredCount - currentCount);
                      return remainingCount > 0 ? 'gray' : 'primary';
                    }

                    const remainingCount = Math.max(0, optionFormData.totalAcquiredCount - filledInputCount);
                    return remainingCount > 0 ? 'gray' : 'primary';
                  })()}
                  disabled={(() => {
                    if (!optionFormData) return true;

                    if (optionFormData.optionInfo.type === 'selectable') {
                      return totalCount !== optionFormData.totalAcquiredCount;
                    }

                    if (optionFormData.optionInfo.type === 'named_selectable') {
                      // stepが'quantity'の場合は数量入力段階なのでtotalCountをチェック
                      // stepが'name'の場合は宛名入力段階なのでfilledInputCountをチェック
                      const currentCount = step === 'quantity' ? totalCount : filledInputCount;
                      const remainingCount = Math.max(0, optionFormData.totalAcquiredCount - currentCount);
                      return remainingCount > 0;
                    }

                    const remainingCount = Math.max(0, optionFormData.totalAcquiredCount - filledInputCount);
                    return remainingCount > 0;
                  })()}
                >
                  入力を完了する
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default OptionBar;
